import React, { useMemo, useState } from "react";
import { Check, Search } from "lucide-react";
import AddButton from "@/components/Core/AddButton";
import { DEFAULT_TAG_COLOR } from "@/components/Shared/TagChip/TagChip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { contrastTextColor } from "@/utils/color.utils";

export interface TagSelectOption {
	key: string;
	/** Display name (already resolved to the current locale). */
	name: string;
	/** Optional background color (hex #RRGGBB) from the tag type. */
	color?: string;
}

interface TagSelectPopoverProps {
	/** All assignable tag types. */
	options: TagSelectOption[];
	/** Keys of the tags currently assigned. */
	selectedKeys: string[];
	/** Assign a tag. */
	onSelect: (key: string) => void;
	/** Unassign a tag. */
	onDeselect: (key: string) => void;
	/** Dashed trigger button label. Defaults to "Lägg till tagg". */
	triggerLabel?: string;
	/** Heading shown above the list. Defaults to "Taggar". */
	heading?: string;
	/** Search field placeholder. Defaults to "Sök tagg…". */
	searchPlaceholder?: string;
	disabled?: boolean;
	align?: "start" | "center" | "end";
	/** Extra classes merged onto the dashed trigger button (e.g. to shrink it). */
	triggerClassName?: string;
	/** Size of the trigger's leading plus icon in px. */
	triggerIconSize?: number;
	/**
	 * Custom trigger element, rendered via `PopoverTrigger asChild` instead of the default dashed
	 * {@link AddButton} (e.g. a clickable tag row). Must be a single element that forwards refs/props.
	 */
	trigger?: React.ReactNode;
}

/**
 * Trello-style label picker: a dashed {@link AddButton} that opens a popover
 * listing every tag type as a colored bar with a checkbox. Checking assigns
 * the tag, unchecking removes it — toggling many without closing. A search box
 * filters long taxonomies. Domain-free: pass resolved `{ key, name, color }`
 * options plus select/deselect callbacks; the caller owns persistence (and any
 * validity/expiry bookkeeping).
 */
const TagSelectPopover: React.FC<TagSelectPopoverProps> = ({
	options,
	selectedKeys,
	onSelect,
	onDeselect,
	triggerLabel = "Lägg till tagg",
	heading = "Taggar",
	searchPlaceholder = "Sök tagg…",
	disabled,
	align = "start",
	triggerClassName,
	triggerIconSize,
	trigger,
}) => {
	const [query, setQuery] = useState("");

	const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return options;
		return options.filter((o) => o.name.toLowerCase().includes(q));
	}, [options, query]);

	return (
		<Popover>
			<PopoverTrigger asChild>
				{trigger ?? (
					<AddButton
						label={triggerLabel}
						disabled={disabled}
						className={triggerClassName}
						iconSize={triggerIconSize}
					/>
				)}
			</PopoverTrigger>
			<PopoverContent align={align} className="w-72 p-0">
				<div className="border-b border-gray-200 p-2.5">
					<div className="relative">
						<Search
							size={14}
							className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
						/>
						<input
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={searchPlaceholder}
							autoFocus
							className="w-full rounded-md border border-solid border-gray-300 bg-white py-1.5 pl-8 pr-2.5 text-sm text-gray-800 outline-none focus:border-brand"
						/>
					</div>
				</div>

				<div className="max-h-72 overflow-y-auto p-2">
					<p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
						{heading}
					</p>
					{filtered.length === 0 ? (
						<p className="px-1 py-2 text-sm text-gray-500">Inga taggar matchar.</p>
					) : (
						<ul className="flex flex-col gap-1">
							{filtered.map((option) => {
								const isSelected = selected.has(option.key);
								const background = option.color || DEFAULT_TAG_COLOR;
								const textColor = contrastTextColor(background);
								return (
									<li key={option.key}>
										<button
											type="button"
											role="checkbox"
											aria-checked={isSelected}
											disabled={disabled}
											onClick={() => (isSelected ? onDeselect(option.key) : onSelect(option.key))}
											className="flex w-full items-center gap-2 rounded border-0 bg-transparent p-0 text-left disabled:pointer-events-none disabled:opacity-50"
										>
											<span
												className={cn(
													"flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
													isSelected
														? "border-brand bg-brand text-white"
														: "border-gray-300 bg-white"
												)}
											>
												{isSelected && <Check size={12} strokeWidth={3} />}
											</span>
											<span
												className="min-w-0 flex-1 truncate rounded px-3 py-1.5 text-sm font-medium"
												style={{ backgroundColor: background, color: textColor }}
											>
												{option.name}
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default TagSelectPopover;
